import cx from 'classnames';
import { arrayOf, bool, func, number, shape, string } from 'prop-types';
import {
  campaignState,
  getBoxOpenStatus,
  getSeasonalBoxCustomizeOpenStatus,
} from '../../../utils/campaignState';
import env from '../../../utils/env';
import OrderSummary from '../SharedComponents/OrderSummary/OrderSummary';
import ReactivateCTA from '../SharedComponents/ReactivateCTA/ReactivateCTA';
import ActionCta from './components/ActionCta/ActionCta';
import * as styles from './BoxSubscriptionPanel.module.css';

const BEAUTY_BOX_OPEN_STATUS_COPY =
  'You have until the end of the month to customize your box. Don’t worry if you forget to customize. We’ll surprise you!';
const BEAUTY_BOX_CLOSED_STATUS_COPY =
  'Thanks for submitting your choices! We’re hard at work processing your box now. You can view tracking here as soon as it’s available.';

const BoxSubscriptionPanel = ({ boxSubscription, sysdata, products = [], userExistingCart }) => {
  const handleEditClick = (choiceNumber) => {
    window.location = `${env.envURL}/customize-box/${boxSlug}?q=${choiceNumber}`;
  };

  const handleManageSubscriptionClick = () => {
    window.location = `${env.envURL}/my-account`;
  };

  const renderProductItem = (product) => {
    const hasMultipleVariants = product?.variants?.length > 1;
    const variants = product?.variants ?? [];
    const imageUrl =
      variants[
        hasMultipleVariants
          ? variants.findIndex((variant) => variant.productUuid === product.choiceProductUuid)
          : 0
      ]?.imageUrl;
    return (
      <div key={product?.id} className={styles.productItem}>
        <img
          alt={product?.name}
          className={styles.productImage}
          src={!product?.choiceProductUuid ? null : imageUrl}
        />
        <div className={styles.productDetails}>
          <h4 className={styles.productName}>Customization {product.choiceNumber}</h4>
          <span className={styles.productDescription}>{product?.name}</span>
          {!isBeautyEditBox && product?.choiceType && (
            <span className={styles.productMeta}>
              {product.choiceQuantity} x{' '}
              {product.choiceType.charAt(0).toUpperCase() +
                product.choiceType.slice(1).toLowerCase()}{' '}
              Choice
              {product.choiceUnitPrice > 0 && ` - $${product.choiceUnitPrice}`}
            </span>
          )}
          {isOpen && (
            <button
              type="button"
              className={styles.editLink}
              onClick={() => {
                handleEditClick(product.choiceNumber);
              }}
            >
              Edit
            </button>
          )}
        </div>
      </div>
    );
  };

  //TODO: Analyze status handling, we decided to omit it for now as per discussed in
  // https://fabfitfun.slack.com/archives/C09AXQRGTDL/p1763579800116639

  const isSeasonalBox = boxSubscription.customizeSaleType === 'seasonal_customize_box';
  const status = isSeasonalBox
    ? getSeasonalBoxCustomizeOpenStatus(boxSubscription, sysdata.boxData)
    : getBoxOpenStatus(boxSubscription);
  const isOpen = status === campaignState.OPEN;
  const displayProducts = products.length > 0 ? products : [];
  const hasProducts = displayProducts.length > 0 && boxSubscription.choices.length > 0;
  const boxSlug =
    boxSubscription?.brandName?.toLowerCase().replace(/\s+/g, '-') +
    '-' +
    boxSubscription?.name?.toLowerCase().replace(/\s+/g, '-');

  const isBeautyEditBox = boxSubscription.customizeSaleType === 'beauty_box';

  const generateChoiceGroups = (choices = []) => {
    // TODO: update this logic to use data from customizeSaleInfo instead (max choiceNumber of catalogs with CHOICE type)
    // However, this would require an additional API call, and we are trying to minimize impact to this page. Defaulting to 1 for unknwon sale types.
    const isSeasonalBox = boxSubscription.customizeSaleType === 'seasonal_customize_box';
    const TOTAL_CHOICE_GROUPS = isBeautyEditBox ? 4 : isSeasonalBox ? 6 : 1;
    const groups = Array.from({ length: TOTAL_CHOICE_GROUPS }, (_, i) => ({
      choiceNumber: i + 1,
      items: [],
    }));

    choices.forEach((choice) => {
      if (choice.choiceNumber >= 1 && choice.choiceNumber <= TOTAL_CHOICE_GROUPS) {
        groups[choice.choiceNumber - 1].items.push(choice);
      }
    });

    groups.forEach((group) => {
      group.items.sort((a, b) => {
        if (a.choiceType === 'PRIMARY' && b.choiceType !== 'PRIMARY') return -1;
        if (a.choiceType !== 'PRIMARY' && b.choiceType === 'PRIMARY') return 1;
        return (a.unitPrice ?? 0) - (b.unitPrice ?? 0);
      });
    });

    return groups;
  };

  const choiceGroups = generateChoiceGroups(boxSubscription.choices);

  const sortedProducts = choiceGroups.flatMap((group) => {
    if (group.items.length === 0) {
      return [
        {
          choiceNumber: group.choiceNumber,
          choiceProductUuid: null,
        },
      ];
    }

    return group.items.map((choice) => {
      const matchedProduct = displayProducts.find((product) => {
        const productMatch = product.productUuid === choice.productUuid;
        const variantMatch = product.variants?.some(
          (variant) => variant.productUuid === choice.productUuid,
        );
        return productMatch || variantMatch;
      });

      const matchingVariant = matchedProduct?.variants?.find(
        (variant) => variant.productUuid === choice.productUuid,
      );

      return {
        ...(matchedProduct ?? {}),
        choiceProductUuid: choice.productUuid,
        choiceNumber: choice.choiceNumber,
        catalogUuid: matchingVariant?.catalogUuid,
        choiceType: choice.choiceType,
        choiceQuantity: choice.quantity,
        choiceUnitPrice: choice.unitPrice,
      };
    });
  });

  const paidChoices = sortedProducts.filter((p) => p.choiceUnitPrice > 0);
  const orderSummaryTotal = paidChoices.reduce((acc, p) => acc + (p.choiceQuantity ?? 1), 0);
  const orderSummarySum = paidChoices.reduce(
    (acc, p) => acc + p.choiceUnitPrice * (p.choiceQuantity ?? 1),
    0,
  );
  const hasAdditionalPricing = orderSummaryTotal > 0;

  const hasPartialSelections = sortedProducts.some((product) => product.choiceProductUuid === null);
  const boxSubscriptionsCopy = isBeautyEditBox
    ? status === campaignState.OPEN
      ? BEAUTY_BOX_OPEN_STATUS_COPY
      : BEAUTY_BOX_CLOSED_STATUS_COPY
    : undefined;

  return (
    <>
      <div className={styles.panelContainer}>
        <div className={styles.titleContainer}>
          <h1 className={styles.title}>{boxSubscription.name}</h1>
          <div className={styles.manageSubscriptionContainer}>
            <button
              type="button"
              className={styles.manageSubscription}
              onClick={handleManageSubscriptionClick}
            >
              Manage Subscription
            </button>
          </div>
        </div>

        {/* Customizations Section */}
        <div className={styles.customizationsSection}>
          <h2 className={styles.customizationsHeader}>Customizations</h2>
          {boxSubscriptionsCopy && <p className={styles.description}>{boxSubscriptionsCopy}</p>}
          <div className={styles.statusContainer}>
            <div className={styles.status}>
              Status: {status || 'Open'}
              <div
                className={cx(styles.statusLight, {
                  [styles.open]: isOpen,
                })}
              />
            </div>
          </div>
        </div>

        {/* Main Container - matches Panel structure */}
        <div className={cx(styles.container, { [styles.centered]: !hasProducts })}>
          <div className={styles.productGridWrapper}>
            {!hasProducts ? (
              <ReactivateCTA
                baseClass="customize"
                title="Customizations are Open!"
                desc="This is where you’ll see your box customizations after you’ve selected them."
                ctaLabel="Customize Now"
                iconClass="fa-hand-holding-box"
                isMobile={sysdata.isMobile}
                isCustomizationFlow
                boxSlug={boxSlug}
              />
            ) : (
              <div className={styles.productGrid}>{sortedProducts.map(renderProductItem)}</div>
            )}
          </div>
          {hasProducts && (
            <div className={styles.summaryDetails}>
              {hasAdditionalPricing && (
                <OrderSummary
                  title="Extra Choices"
                  total={orderSummaryTotal}
                  sum={orderSummarySum}
                  country={sysdata.userData?.shippingAddress?.country ?? ''}
                  footnote="Total does not reflect credit applied."
                />
              )}
              <ActionCta
                hasPartialSelections={hasPartialSelections}
                hasAdditionalPricing={hasAdditionalPricing}
                boxSlug={boxSlug}
                isOpen={isOpen}
                isProcessBoxEnabled={isBeautyEditBox}
                userExistingCart={userExistingCart}
                boxSubscription={boxSubscription}
                sortedProducts={sortedProducts}
              />
            </div>
          )}
        </div>
      </div>
      <div className="hor-rule mar-h-40" />
    </>
  );
};

BoxSubscriptionPanel.propTypes = {
  boxSubscription: shape({}).isRequired,
  sysdata: shape({
    boxData: shape({
      customizeCampaignState: shape({}).isRequired,
      customizations: arrayOf(
        shape({
          additionalItems: arrayOf(
            shape({
              quantity: number.isRequired,
              price: number.isRequired,
            }),
          ).isRequired,
          questionType: string.isRequired,
          additionalItemCost: number.isRequired,
        }),
      ).isRequired,
    }).isRequired,
    userData: shape({}).isRequired,
    isMobile: bool.isRequired,
    isProspect: bool.isRequired,
    revealCallback: func.isRequired,
  }).isRequired,
  products: arrayOf(shape({})),
  userExistingCart: shape({}),
};

BoxSubscriptionPanel.displayName = 'BoxSubscriptionPanel';

export default BoxSubscriptionPanel;
